import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { useProductStore } from '@/shared/pinia/modules/product';
import { Strategy } from '@/shared/strategies';
import {
  isDesktopView,
  isDesktopXLView,
  isTabletView
} from '@/shared/utils/dom';
import { updateUrlParameter } from '@/shared/utils/urls';

const blocksNumber = {
  [Strategy.ProductPage]: {
    smScreen: 4,
    mdScreen: 5,
    lgScreen: 6,
    xlScreen: 7
  }
};

const isValidStrategy = (
  strategy: string
): strategy is keyof typeof blocksNumber => {
  return strategy in blocksNumber;
};

export const useProductOptionsStore = defineStore('product-options', () => {
  const activeOption = ref<HydratedShopifyVariant | null>(null);
  const options = ref<HydratedShopifyVariant[]>([]);
  const kind = ref(null);
  const showOptionsModal = ref(false);
  const modalOptionsPosition = ref(0);
  const haveDifferentPrices = ref(false);

  const targetVariant = computed(() => {
    return (
      activeOption.value ??
      options.value.find(
        (variant) => variant.id === window.Hobbii?.productPage?.targetVariantId
      ) ??
      options.value[0]
    );
  });

  const hasOptions = computed(() => options.value.length > 0 && !!kind.value);

  const productInStock = computed(() => {
    if (activeOption.value?.id) {
      return activeOption.value.inStock;
    } else {
      return optionsInStock.value && optionsInStock.value.length != 0;
    }
  });

  const optionsInStock = computed(() =>
    options.value.filter((option) => option.inStock)
  );

  const optionsOutOfStock = computed(() =>
    options.value.filter((option) => !option.inStock)
  );

  const blocksNumberCalculation = (screenSize: number) => {
    const productStore = useProductStore();

    const desktopView = isDesktopView(screenSize);
    const desktopXLView = isDesktopXLView(screenSize);
    const tabletView = isTabletView(screenSize);

    const strategy = productStore.getStrategy;
    const strategyBlocks = isValidStrategy(strategy)
      ? blocksNumber[strategy]
      : blocksNumber[Strategy.ProductPage];

    let blkNum = strategyBlocks.smScreen;
    if (tabletView) {
      blkNum = strategyBlocks.mdScreen;
    } else if (desktopView && !desktopXLView) {
      blkNum = strategyBlocks.lgScreen;
    } else if (desktopXLView) {
      blkNum = strategyBlocks.xlScreen;
    }

    return blkNum;
  };

  const isOptionSelected = computed(
    () => (hasOptions.value && !!activeOption.value?.id) || !hasOptions.value
  );

  const updatePhotoGallery = (option: HydratedShopifyVariant) => {
    window.dispatchEvent(
      new CustomEvent('changed.hobbii.product.image', {
        detail: {
          imageUrl: option.consolidatedImageUrl
        }
      })
    );
  };

  const updateActiveOption = (option: HydratedShopifyVariant) => {
    const productStore = useProductStore();

    updateUrlParameter('variant', option.id.toString());
    activeOption.value = option;
    updatePageTitle(option.title);
    updatePhotoGallery(option);

    if (haveDifferentPrices.value) {
      productStore.updatePriceByVariant(option);
    }

    // TODO update stock in physical stores setAvailableInStores()
    // TODO update datalayer
    // TODO update stock
  };

  const productIsDiscontinued = computed(() => {
    return options.value.every((option) => option.discontinued);
  });

  return {
    activeOption,
    options,
    kind,
    showOptionsModal,
    modalOptionsPosition,
    haveDifferentPrices,
    targetVariant,
    productInStock,
    optionsInStock,
    optionsOutOfStock,
    blocksNumberCalculation,
    isOptionSelected,
    updateActiveOption,
    productIsDiscontinued
  };
});

const updatePageTitle = (optionTitle: string) => {
  const separator = ' - ';

  const titleParts = document.title.split(separator);
  const replaceName = titleParts.length === 3;
  titleParts.splice(1, replaceName ? 1 : 0, optionTitle);

  document.title = titleParts.join(separator);
};
